/**
 * Call detail → Quality Management tab.
 *
 * Shows the call's QM evaluation(s) — normally the one the QM Auditor AI agent
 * filled in from the transcript against the tenant's default QM form — with
 * per-item scores, evidence quotes and coaching notes. Polls while the AI is
 * still scoring, so the result appears without a reload.
 *
 * Supervisors / admins can adjust scores and comments (the total is
 * recomputed with the same shared scorer the server uses) or re-run the AI.
 * Agents see their evaluation read-only.
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import {
  AlertCircle, Bot, Check, ClipboardCheck, Loader2, Pencil, Quote, RefreshCw, ShieldAlert, ThumbsUp, TrendingUp, UserCheck, X,
} from 'lucide-react'
import { scoreQmAnswers, type QmAnswer, type QmFormItemSnapshot } from '@kc3/types'
import { QmScoreRing } from '../../quality/QmScoreRing.js'
import { AiDraftBadge, DisputeScoreControl } from '../../quality/QmOversight.js'
import { api } from '../../../api/client.js'
import { useAuthStore } from '../../../store/authStore.js'
import { formatDateTime } from '../../../utils/formatDate.js'

interface Evaluation {
  id: string
  formId: string
  formName: string
  formSnapshot: QmFormItemSnapshot[]
  source: 'ai' | 'manual'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'
  answers: QmAnswer[]
  totalScore: number | null
  maxScore: number | null
  percentage: number | null
  passed: boolean | null
  passingScore: number | null
  summary: string | null
  strengths: string[]
  improvements: string[]
  aiModel: string | null
  error: string | null
  aiDraft?: boolean
  humanReviewed?: boolean
  reviewedAt: string | null
  reviewedByName: string | null
  completedAt: string | null
}

interface Response {
  evaluations: Evaluation[]
  defaultForm: { id: string; name: string; status: boolean; autoEvaluate: boolean; passingScore: number; itemCount: number } | null
  forms: Array<{ id: string; name: string; isDefault: boolean }>
}

const REVIEW_PERMISSIONS = ['supervisor:view', 'admin:settings:manage', '*']

function useCanReview(): boolean {
  const user = useAuthStore((s) => s.user)
  if (!user) return false
  if (user.isSuperAdmin) return true
  return REVIEW_PERMISSIONS.some((p) => user.permissions?.includes(p) ?? false)
}

function ScoreRing(props: { percentage: number; passed: boolean | null }): JSX.Element {
  return <QmScoreRing {...props} size={96} />
}

function Bar({ score, weight }: { score: number | null; weight: number }): JSX.Element {
  if (score === null) return <span className="text-[11px] font-semibold text-slate-400">N/A</span>
  const pct = weight > 0 ? (score / weight) * 100 : 0
  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : pct > 0 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <span className="w-12 text-right text-xs font-semibold tabular-nums text-slate-800">{score}/{weight}</span>
    </div>
  )
}

function Empty({ icon, title, children, action }: { icon: JSX.Element; title: string; children?: React.ReactNode; action?: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <span className="text-slate-300">{icon}</span>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      {children && <div className="max-w-lg text-xs text-slate-500">{children}</div>}
      {action}
    </div>
  )
}

export function QmEvaluationPanel({ callId, hasTranscript }: { callId: string; hasTranscript: boolean }): JSX.Element {
  const canReview = useCanReview()
  const [data, setData] = useState<Response | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<QmAnswer[]>([])
  const [draftSummary, setDraftSummary] = useState('')
  const [busy, setBusy] = useState(false)
  const [version, setVersion] = useState(0)

  const reload = useCallback(() => setVersion((v) => v + 1), [])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let polls = 0
    const load = (): void => {
      void api.get<Response>(`/qm/evaluations/call/${callId}`)
        .then((d) => {
          if (cancelled) return
          setData(d)
          setError(null)
          const pending = d.evaluations.some((e) => e.status === 'PENDING' || e.status === 'IN_PROGRESS')
          // Auto-evaluation will be queued within a minute of the transcript.
          const awaitingQueue = d.evaluations.length === 0 && hasTranscript && d.defaultForm?.status && d.defaultForm.autoEvaluate
          if ((pending || awaitingQueue) && polls < 120) {
            polls++
            timer = setTimeout(load, 5_000)
          }
        })
        .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the QM evaluation.') })
    }
    load()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [callId, hasTranscript, version])

  const evaluation = useMemo(
    () => data?.evaluations.find((e) => e.id === selectedId) ?? data?.evaluations[0] ?? null,
    [data, selectedId],
  )

  const live = useMemo(() => {
    if (!evaluation) return null
    return editing
      ? scoreQmAnswers(evaluation.formSnapshot, draft, evaluation.passingScore ?? 70)
      : null
  }, [editing, draft, evaluation])

  const rerun = async (formId?: string): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/qm/evaluations/call/${callId}/rerun`, formId ? { formId } : {})
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not queue the AI evaluation.')
    } finally {
      setBusy(false)
    }
  }

  const startEdit = (): void => {
    if (!evaluation) return
    setDraft(evaluation.formSnapshot.map((it) => {
      const a = evaluation.answers.find((x) => x.itemId === it.id)
      return { itemId: it.id, score: a?.score ?? 0, ...(a?.notApplicable ? { notApplicable: true } : {}), comment: a?.comment ?? '', ...(a?.evidence ? { evidence: a.evidence } : {}) }
    }))
    setDraftSummary(evaluation.summary ?? '')
    setEditing(true)
  }

  const saveReview = async (submit: boolean): Promise<void> => {
    if (!evaluation) return
    setBusy(true)
    setError(null)
    try {
      await api.put(`/qm/evaluations/${evaluation.id}`, { answers: draft, summary: draftSummary, ...(submit ? { submit: true } : {}) })
      setEditing(false)
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the review.')
    } finally {
      setBusy(false)
    }
  }

  const patchDraft = (itemId: string, patch: Partial<QmAnswer>): void =>
    setDraft((d) => d.map((a) => (a.itemId === itemId ? { ...a, ...patch } : a)))

  if (!data && !error) return <Empty icon={<Loader2 className="h-8 w-8 animate-spin" />} title="Loading quality evaluation…" />

  const errorBox = error && (
    <div className="mb-3 flex items-center gap-2 rounded-[3px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
      <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
    </div>
  )

  if (!data) return <>{errorBox}</>

  if (!evaluation) {
    if (!data.defaultForm) {
      return (
        <>{errorBox}
          <Empty icon={<ClipboardCheck className="h-8 w-8" />} title="No default QM form">
            An admin needs to create a QM form and mark it as default in <b>Admin → Quality Management → QM Forms</b>.
            The QM Auditor AI agent then scores every transcribed call automatically.
          </Empty>
        </>
      )
    }
    const df = data.defaultForm
    const runButton = canReview && hasTranscript && (
      <button type="button" onClick={() => void rerun()} disabled={busy}
        className="mt-2 inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />} Run AI evaluation now
      </button>
    )
    return (
      <>{errorBox}
        {!hasTranscript ? (
          <Empty icon={<ClipboardCheck className="h-8 w-8" />} title={`Default form: ${df.name}`}>
            The AI scores the call from its transcript — it will be evaluated as soon as the transcript is ready.
          </Empty>
        ) : df.autoEvaluate && df.status ? (
          <Empty icon={<Loader2 className="h-8 w-8 animate-spin text-blue-400" />} title={`Queued for AI evaluation · ${df.name}`} action={runButton}>
            The QM Auditor AI agent picks up new transcripts every minute.
          </Empty>
        ) : (
          <Empty icon={<ClipboardCheck className="h-8 w-8" />} title={`Default form: ${df.name}`} action={runButton}>
            AI auto-evaluation is off for this form.
          </Empty>
        )}
      </>
    )
  }

  if (evaluation.status === 'PENDING' || evaluation.status === 'IN_PROGRESS') {
    return (
      <>{errorBox}
        <Empty icon={<Loader2 className="h-8 w-8 animate-spin text-blue-400" />} title={`QM Auditor AI agent is scoring this call · ${evaluation.formName}`}>
          {evaluation.status === 'PENDING' ? 'Queued — scoring starts within a minute.' : 'Reading the transcript and filling in the form…'}
          {evaluation.error && <span className="mt-2 block rounded-[3px] bg-amber-50 px-3 py-2 text-amber-800">Last attempt: {evaluation.error}</span>}
        </Empty>
      </>
    )
  }

  if (evaluation.status === 'FAILED') {
    return (
      <>{errorBox}
        <Empty icon={<AlertCircle className="h-8 w-8 text-red-400" />} title="AI evaluation failed"
          action={canReview && (
            <button type="button" onClick={() => void rerun(evaluation.formId)} disabled={busy}
              className="mt-2 inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Retry
            </button>
          )}>
          {evaluation.error && <span className="block rounded-[3px] bg-red-50 px-3 py-2 text-red-700">{evaluation.error}</span>}
        </Empty>
      </>
    )
  }

  // ── COMPLETED ──
  const answers = editing ? draft : evaluation.answers
  const pct = live?.percentage ?? evaluation.percentage ?? 0
  const passed = live ? live.passed : evaluation.passed
  const sections = evaluation.formSnapshot.reduce<Array<{ name: string; items: QmFormItemSnapshot[] }>>((acc, it) => {
    const s = acc.find((x) => x.name === it.section)
    if (s) s.items.push(it)
    else acc.push({ name: it.section, items: [it] })
    return acc
  }, [])
  const criticalFailed = new Set((live ?? scoreQmAnswers(evaluation.formSnapshot, evaluation.answers, evaluation.passingScore ?? 70)).criticalFailures)

  return (
    <div className="space-y-4">
      {errorBox}

      {data.evaluations.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {data.evaluations.map((e) => (
            <button key={e.id} type="button" onClick={() => { setSelectedId(e.id); setEditing(false) }}
              className={`rounded-[3px] px-2.5 py-1 text-xs font-semibold ${e.id === evaluation.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {e.formName}{e.percentage !== null ? ` · ${Math.round(e.percentage)}%` : ''}
            </button>
          ))}
        </div>
      )}

      {/* Scorecard header */}
      <div className="flex flex-wrap items-center gap-5 rounded-[3px] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4">
        <ScoreRing percentage={pct} passed={passed} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-base font-semibold text-slate-900">{evaluation.formName}</h4>
            <span className={`rounded-[3px] px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset ${passed ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-red-50 text-red-700 ring-red-200'}`}>
              {passed ? 'PASS' : 'FAIL'}
            </span>
            <AiDraftBadge aiDraft={evaluation.aiDraft} humanReviewed={evaluation.humanReviewed} />
            {criticalFailed.size > 0 && (
              <span className="inline-flex items-center gap-1 rounded-[3px] bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                <ShieldAlert className="h-3 w-3" /> Critical item failed
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">
            Score <b className="tabular-nums text-slate-900">{Math.round(pct)}%</b> · pass mark {evaluation.passingScore ?? 70}%
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><Bot className="h-3.5 w-3.5 text-blue-500" /> Filled by QM Auditor AI agent{evaluation.aiModel ? ` (${evaluation.aiModel})` : ''}</span>
            {evaluation.completedAt && <span>{formatDateTime(evaluation.completedAt)}</span>}
            {evaluation.reviewedAt && (
              <span className="inline-flex items-center gap-1 text-emerald-700"><UserCheck className="h-3.5 w-3.5" /> Reviewed{evaluation.reviewedByName ? ` by ${evaluation.reviewedByName}` : ''} · {formatDateTime(evaluation.reviewedAt)}</span>
            )}
          </p>
        </div>
        {canReview && !editing && (
          <div className="flex gap-2">
            <button type="button" onClick={startEdit} className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              <Pencil className="h-3.5 w-3.5" /> Review
            </button>
            <button type="button" onClick={() => void rerun(evaluation.formId)} disabled={busy} title="Score again with the AI"
              className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-run AI
            </button>
          </div>
        )}
        {editing && (
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 rounded-[3px] px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button type="button" onClick={() => void saveReview(false)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save draft
              </button>
              <button type="button" onClick={() => void saveReview(true)} disabled={busy || live === null || live.percentage !== 100 || live.criticalFailures.length > 0}
                title="The evaluation can only be submitted at a perfect 100% score"
                className="inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Submit
              </button>
            </div>
            {live !== null && live.percentage !== 100 && (
              <p className="text-[11px] text-amber-600">Submit is enabled only at a perfect 100% score (currently {Math.round(live.percentage)}%).</p>
            )}
          </div>
        )}
      </div>

      {!canReview && !editing && <DisputeScoreControl evaluationId={evaluation.id} />}

      {/* Summary + coaching */}
      {(editing || evaluation.summary || evaluation.strengths.length > 0 || evaluation.improvements.length > 0) && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="rounded-[3px] border border-slate-200 bg-white p-3 lg:col-span-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Summary</p>
            {editing ? (
              <textarea value={draftSummary} onChange={(e) => setDraftSummary(e.target.value)}
                className="block min-h-[60px] w-full rounded-[3px] border border-slate-200 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none" />
            ) : (
              <p className="text-sm leading-relaxed text-slate-700">{evaluation.summary ?? '—'}</p>
            )}
          </div>
          {evaluation.strengths.length > 0 && (
            <div className="rounded-[3px] border border-emerald-100 bg-emerald-50/50 p-3 lg:col-span-1">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"><ThumbsUp className="h-3.5 w-3.5" /> Strengths</p>
              <ul className="space-y-1 text-sm text-slate-700">{evaluation.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul>
            </div>
          )}
          {evaluation.improvements.length > 0 && (
            <div className="rounded-[3px] border border-amber-100 bg-amber-50/50 p-3 lg:col-span-2">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700"><TrendingUp className="h-3.5 w-3.5" /> Coaching opportunities</p>
              <ul className="space-y-1 text-sm text-slate-700">{evaluation.improvements.map((s, i) => <li key={i}>• {s}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {/* The form, filled in */}
      {sections.map((section) => {
        const sTotal = section.items.reduce((n, it) => n + (answers.find((a) => a.itemId === it.id)?.score ?? 0), 0)
        const sMax = section.items.reduce((n, it) => n + (answers.find((a) => a.itemId === it.id)?.notApplicable ? 0 : it.weight), 0)
        return (
          <div key={section.name} className="overflow-hidden rounded-[3px] border border-slate-200">
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2">
              <span className="text-sm font-semibold text-slate-800">{section.name}</span>
              <span className="text-xs font-semibold tabular-nums text-slate-500">{sTotal} / {sMax}</span>
            </div>
            <ul className="divide-y divide-slate-100">
              {section.items.map((it) => {
                const a = answers.find((x) => x.itemId === it.id)
                const na = a?.notApplicable === true
                return (
                  <li key={it.id} className={`px-3 py-2.5 ${criticalFailed.has(it.id) ? 'bg-red-50/40' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">
                          {it.question}
                          {it.isCritical && <span className="ml-1.5 rounded-[2px] bg-red-50 px-1 text-[10px] font-bold uppercase text-red-600">Critical</span>}
                        </p>
                        {!editing && a?.comment && <p className="mt-0.5 text-xs text-slate-600">{a.comment}</p>}
                        {!editing && a?.evidence && (
                          <p className="mt-1 flex items-start gap-1 text-xs italic text-slate-500"><Quote className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-300" /> {a.evidence}</p>
                        )}
                      </div>
                      {editing ? (
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                            <input type="checkbox" className="h-3.5 w-3.5" checked={na}
                              onChange={(e) => patchDraft(it.id, e.target.checked ? { notApplicable: true, score: null } : { notApplicable: false, score: 0 })} /> N/A
                          </label>
                          <input type="number" min={0} max={it.weight} disabled={na} value={na ? '' : a?.score ?? 0}
                            onChange={(e) => patchDraft(it.id, { score: Math.max(0, Math.min(it.weight, Number(e.target.value) || 0)) })}
                            className="w-16 rounded-[3px] border border-slate-200 px-2 py-1 text-right text-sm tabular-nums focus:border-blue-500 focus:outline-none disabled:bg-slate-50" />
                          <span className="text-xs text-slate-400">/ {it.weight}</span>
                        </div>
                      ) : (
                        <Bar score={na ? null : a?.score ?? 0} weight={it.weight} />
                      )}
                    </div>
                    {editing && (
                      <input value={a?.comment ?? ''} onChange={(e) => patchDraft(it.id, { comment: e.target.value })} placeholder="Comment"
                        className="mt-2 block w-full rounded-[3px] border border-slate-200 px-2.5 py-1 text-xs focus:border-blue-500 focus:outline-none" />
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
